import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { AssessmentAnswerInterface } from 'app/assessment/models';
import { ErrorMessage } from 'app/core/error-handler/constants';
import { Error } from 'app/core/error-handler/models';
import { createErrorRaised } from 'app/core/error-handler/state/actions';
import { EquipmentRestConnectorService } from 'app/equipment/services';
import {
  EquipmentActionType,
  LoadEmployeeEquipmentWorkflows,
  LoadEmployeeEquipmentWorkflowsCompleted,
  LoadEquipmentLibrary,
  LoadEquipmentLibraryCompleted,
  LoadEquipmentLibraryItem,
  LoadEquipmentLibraryItemCompleted,
  LoadWorkAreasCompleted,
  LoadWorkflowStatusTypesCompleted,
 } from 'app/equipment/state/actions';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import {
  EmployeeEquipmentWorkflowInterface,
  EmployeeEquipmentWorkflowStatusInterface,
  EmployeeEquipmentWorkflowStatusTypeInterface,
  EquipmentCategoryInterface,
  EquipmentInterface } from '../models';

import {
  AddNewStatusToEmployeeEquipmentWorkflow,
  AddNewStatusToEmployeeEquipmentWorkflowCompleted,
  CreateEmployeeEquipmentWorkflow,
  CreateEmployeeEquipmentWorkflowCompleted,
  CreateEquipmentCategory,
  CreateEquipmentCategoryCompleted,
  CreateEquipmentLibraryItem,
  CreateEquipmentLibraryItemCompleted } from './actions/equipment-create.action';
import {
  DeleteEmployeeEquipmentWorkflowCompleted,
  DeleteEquipmentLibraryCategory,
  DeleteEquipmentLibraryCategoryCompleted,
  DeleteEquipmentLibraryItem,
  DeleteEquipmentLibraryItemCompleted } from './actions/equipment-delete.actions';
import {
  UpdateEmployeeEquipmentItem,
  UpdateEmployeeEquipmentItemCompleted,
  UpdateEmployeeEquipmentWorkFlowStatus,
  UpdateEmployeeEquipmentWorkFlowStatusCompleted,
  UpdateEquipmentCategory,
  UpdateEquipmentCategoryCompleted,
  UpdateEquipmentLibraryItem,
  UpdateEquipmentLibraryItemCompleted } from './actions/equipment-update.action';

@Injectable()
export class EquipmentEffects {
  public source: string = 'EquipmentEffects';

  /**
   * Load effects.
   */
  @Effect()
  public loadEquipmentLibrary$: Observable<Action> = this.actions$
    .pipe(
      ofType(EquipmentActionType.LoadEquipmentLibrary),
      switchMap(() => {
        return this._equipmentConnector.loadLibrary().pipe(
          map((libraryItems: EquipmentCategoryInterface[]) => new LoadEquipmentLibraryCompleted(libraryItems)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public loadWorkflowStatusTypes$: Observable<Action> = this.actions$
    .pipe(
      ofType(EquipmentActionType.LoadWorkflowStatusTypes),
      switchMap(() => {
        return this._equipmentConnector.loadEquipmentWorkflowStatusList().pipe(
          map((statusTypeItems: EmployeeEquipmentWorkflowStatusTypeInterface[]) => new LoadWorkflowStatusTypesCompleted(statusTypeItems)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public loadWorkAreas$: Observable<Action> = this.actions$
    .pipe(
      ofType(EquipmentActionType.LoadWorkAreas),
      switchMap(() => {
        return this._equipmentConnector.loadWorkAreaList().pipe(
          map((workAreaItems: AssessmentAnswerInterface[]) => new LoadWorkAreasCompleted(workAreaItems)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public loadEmployeeEquipmentWorkflows$: Observable<Action> = this.actions$
    .pipe(
      ofType(EquipmentActionType.LoadEmployeeEquipmentWorkflows),
      map((action: LoadEmployeeEquipmentWorkflows) => action.payload),
      switchMap((userId: number) => {
        return this._equipmentConnector.loadEmployeeEquipmentWorkflowsForUser(userId).pipe(
          map((workflowItems: EmployeeEquipmentWorkflowInterface[]) => new LoadEmployeeEquipmentWorkflowsCompleted(workflowItems)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public loadEquipmentLibraryItem$: Observable<Action> = this.actions$
    .pipe(
      ofType(EquipmentActionType.LoadEquipmentLibraryItem),
      map((action: LoadEquipmentLibraryItem) => action.payload),
      switchMap((equipmentId: number) => {
        return this._equipmentConnector.loadEquipmentLibraryItem(equipmentId).pipe(
          map((equipmentItem: EquipmentInterface) => new LoadEquipmentLibraryItemCompleted(equipmentItem)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));
  /*
   * Create effects.
  */

  @Effect()
  public createEquipmentCategory$: Observable<Action> = this.actions$
    .pipe(
      ofType(EquipmentActionType.CreateEquipmentCategory),
      map((action: CreateEquipmentCategory) => action.payload),
      switchMap((newCategoryData: EquipmentCategoryInterface) => {
        return this._equipmentConnector.createEquipmentLibraryCategory(newCategoryData).pipe(
          switchMap((categoryData: EquipmentCategoryInterface) => {
            return [
              new CreateEquipmentCategoryCompleted(categoryData),
              new LoadEquipmentLibrary(),
            ];
          }),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public createEquipmentLibraryItem$: Observable<Action> = this.actions$
    .pipe(
      ofType(EquipmentActionType.CreateEquipmentLibraryItem),
      map((action: CreateEquipmentLibraryItem) => action.payload),
      switchMap((newItemData: EquipmentInterface) => {
        return this._equipmentConnector.createEquipmentLibraryItem(newItemData).pipe(
          switchMap((itemData: EquipmentInterface) => {
            return [
              new CreateEquipmentLibraryItemCompleted(itemData),
              new LoadEquipmentLibrary(),
            ];
          }),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public createEmployeeEquipmentWorkflow$: Observable<Action> = this.actions$
    .pipe(
      ofType(EquipmentActionType.CreateEmployeeEquipmentWorkflow),
      map((action: CreateEmployeeEquipmentWorkflow) => action.payload),
      switchMap((newWorkflowData: EmployeeEquipmentWorkflowInterface) => {
        return this._equipmentConnector.createUserEquipmentWorkflow(newWorkflowData).pipe(
          map((workflowData: EmployeeEquipmentWorkflowInterface) => new CreateEmployeeEquipmentWorkflowCompleted(workflowData)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public addNewStatusToEmployeeEquipmentWorkflow$: Observable<Action> = this.actions$
    .pipe(
      ofType(EquipmentActionType.AddNewStatusToEmployeeEquipmentWorkflow),
      map((action: AddNewStatusToEmployeeEquipmentWorkflow) => action.payload),
      switchMap((payloadData: { workflow: EmployeeEquipmentWorkflowInterface, statusRecord: EmployeeEquipmentWorkflowStatusInterface }) => {
        return this._equipmentConnector.addStatusToWorkflow(payloadData.workflow, payloadData.statusRecord).pipe(
          map((workflowData: EmployeeEquipmentWorkflowInterface) => new AddNewStatusToEmployeeEquipmentWorkflowCompleted(workflowData)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  /*
   * Update effects.
   */

  @Effect()
  public updateEquipmentCategory$: Observable<Action> = this.actions$
    .pipe(
      ofType(EquipmentActionType.UpdateEquipmentCategory),
      map((action: UpdateEquipmentCategory) => action.payload),
      switchMap((categoryToUpdate: EquipmentCategoryInterface) => {
        return this._equipmentConnector.updateEquipmentLibraryCategory(categoryToUpdate).pipe(
          map((updatedCategoryData: EquipmentCategoryInterface) => new UpdateEquipmentCategoryCompleted(updatedCategoryData)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public updateEquipmentLibraryItem$: Observable<Action> = this.actions$
    .pipe(
      ofType(EquipmentActionType.UpdateEquipmentLibraryItem),
      map((action: UpdateEquipmentLibraryItem) => action.payload),
      switchMap((itemToUpdate: EquipmentInterface) => {
        return this._equipmentConnector.updateEquipmentLibraryItem(itemToUpdate).pipe(
          map((updatedItemData: EquipmentInterface) => new UpdateEquipmentLibraryItemCompleted(updatedItemData)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public updateEmployeeEquipmentItem$: Observable<Action> = this.actions$
    .pipe(
      ofType(EquipmentActionType.UpdateEmployeeEquipmentItem),
      map((action: UpdateEmployeeEquipmentItem) => action.payload),
      switchMap((workflowToUpdate: EmployeeEquipmentWorkflowInterface) => {
        return this._equipmentConnector.updateEmployeeEquipmentItem(workflowToUpdate).pipe(
          map((updatedWorkflowData: EmployeeEquipmentWorkflowInterface) => new UpdateEmployeeEquipmentItemCompleted(updatedWorkflowData)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public updateEmployeeEquipmentWorkFlowStatus$: Observable<Action> = this.actions$
    .pipe(
      ofType(EquipmentActionType.UpdateEmployeeEquipmentWorkFlowStatus),
      map((action: UpdateEmployeeEquipmentWorkFlowStatus) => action.payload),
      switchMap((payloadData: { workflow: EmployeeEquipmentWorkflowInterface, statusRecord: EmployeeEquipmentWorkflowStatusInterface }) => {
        return this._equipmentConnector.updateEmployeeEquipmentWorkflowStatus(payloadData.workflow, payloadData.statusRecord).pipe(
          map((updatedWorkflowData: EmployeeEquipmentWorkflowInterface) =>
            new UpdateEmployeeEquipmentWorkFlowStatusCompleted(updatedWorkflowData)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

    /*
     * Delete effects.
     */


  @Effect()
  public deleteEquipmentLibraryCategory$: Observable<Action> = this.actions$
    .pipe(
      ofType(EquipmentActionType.DeleteEquipmentLibraryCategory),
      map((action: DeleteEquipmentLibraryCategory) => action.payload),
      switchMap((categoryToDelete: EquipmentCategoryInterface) => {
        return this._equipmentConnector.deleteEquipmentLibraryCategory(categoryToDelete).pipe(
          switchMap((categoryData: EquipmentCategoryInterface) => {
            return [
              new DeleteEquipmentLibraryCategoryCompleted(categoryData),
              new LoadEquipmentLibrary(),
            ];
          }),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public DeleteEquipmentLibraryItem$: Observable<Action> = this.actions$
    .pipe(
      ofType(EquipmentActionType.DeleteEquipmentLibraryItem),
      map((action: DeleteEquipmentLibraryItem) => action.payload),
      switchMap((itemToDelete: EquipmentInterface) => {
        return this._equipmentConnector.deleteEquipmentLibraryItem(itemToDelete).pipe(
          switchMap((categoryData: EquipmentCategoryInterface) => {
            return [
              new DeleteEquipmentLibraryItemCompleted(categoryData),
              new LoadEquipmentLibrary(),
            ];
          }),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public DeleteEmployeeEquipmentWorkflow$: Observable<Action> = this.actions$
    .pipe(
      ofType(EquipmentActionType.DeleteEmployeeEquipmentWorkflow),
      map((action: DeleteEquipmentLibraryItem) => action.payload),
      switchMap((itemToDelete: EmployeeEquipmentWorkflowInterface) => {
        return this._equipmentConnector.deleteEquipmentWorkflow(itemToDelete).pipe(
          switchMap((workflowData: EmployeeEquipmentWorkflowInterface) => {
            return [
              new DeleteEmployeeEquipmentWorkflowCompleted(workflowData),
              new LoadEmployeeEquipmentWorkflows(workflowData.forUser.id),
            ];
          }),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));


  constructor(private readonly actions$: Actions, private readonly _equipmentConnector: EquipmentRestConnectorService) {}
}
